import { AttentiveIntegrationPurpose } from "@redotech/cockroachdb-codegen/congodb/attentive-integration-purpose";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import type { MessageChannelV3 } from "@redotech/redo-flows-common/rules-v3/types";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import { assertNever } from "@redotech/util/type";
import { useMemo } from "react";
import { getAttentiveIntegration } from "../client/attentive";
import { getFacebookIntegrations } from "../client/facebook";
import { getInstagramIntegrations } from "../client/instagram";
import { getPostscriptIntegration } from "../client/postscript";
import { getTiktokIntegration } from "../client/tiktok";
import { EmailIntegrationsContext } from "./email-integrations";

/** The v3 channels a platform covers, for callers that speak MessageChannelV3. */
const platformChannels: Partial<
  Record<ConversationPlatform, readonly MessageChannelV3[]>
> = {
  [ConversationPlatform.EMAIL]: ["gmail", "outlook", "customEmail"],
  [ConversationPlatform.REDO_CHAT]: ["redoChat"],
  [ConversationPlatform.VOICE]: ["voice"],
  [ConversationPlatform.SMS]: ["sms"],
  [ConversationPlatform.FACEBOOK]: ["facebook", "facebookComments"],
  [ConversationPlatform.INSTAGRAM]: ["instagram", "instagramComments"],
  [ConversationPlatform.ATTENTIVE]: ["attentive"],
  [ConversationPlatform.POSTSCRIPT]: ["postscript"],
  [ConversationPlatform.TIKTOK]: ["tiktok"],
};

const CONNECTABLE_PLATFORMS = Object.keys(platformChannels).map(
  (platform) => platform as ConversationPlatform,
);

export interface ChannelConnectivity {
  isPlatformConnected: (platform: ConversationPlatform) => boolean;
  /** Channels the team can actually receive tickets on. */
  connectedChannels: readonly MessageChannelV3[];
  pending: boolean;
}

/** Single source of truth for which support channels a team has connected. */
export function useChannelConnectivity(): ChannelConnectivity {
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);

  const integrationsLoad = useLoad(
    async (signal) => {
      if (!team) {
        return null;
      }

      const [facebook, instagram, postscript, attentive, tiktok] =
        await Promise.all([
          getFacebookIntegrations(client, { signal }),
          getInstagramIntegrations(client, { signal }),
          getPostscriptIntegration(client, { signal }),
          getAttentiveIntegration(client, { signal }),
          getTiktokIntegration(client, { signal }),
        ]);

      return { facebook, instagram, postscript, attentive, tiktok };
    },
    [team, client],
  );

  const isPlatformConnected = useMemo(
    () =>
      (platform: ConversationPlatform): boolean => {
        switch (platform) {
          case ConversationPlatform.EMAIL:
            return (
              (emailIntegrationsLoad.value &&
                Object.values(emailIntegrationsLoad.value).some(
                  (arr) => arr.length > 0,
                )) ||
              false
            );

          case ConversationPlatform.REDO_CHAT:
            return !!team.settings?.customerWidget;

          case ConversationPlatform.VOICE: {
            // Every Telnyx field has to be present: a half-provisioned team
            // can offer a Voice checkbox that routes calls to a number it
            // cannot dial.
            const voice = team.settings.support?.voice;
            return !(
              !voice?.billingGroupId ||
              !voice?.outboundVoiceProfileId ||
              !voice?.sipConnectionCredential ||
              !voice?.sipConnectionId ||
              !voice?.voiceApplicationId
            );
          }

          case ConversationPlatform.SMS:
            return !!team.settings.support?.billing?.textMessaging?.enabled;

          case ConversationPlatform.FACEBOOK:
          case ConversationPlatform.INSTAGRAM_COMMENTS:
          case ConversationPlatform.FACEBOOK_COMMENTS:
          case ConversationPlatform.INSTAGRAM:
            return integrationsLoad.value?.instagram?.integrations?.length > 0;

          case ConversationPlatform.ATTENTIVE:
            return integrationsLoad.value?.attentive?.integration?.purposes?.includes(
              AttentiveIntegrationPurpose.Support,
            );

          case ConversationPlatform.POSTSCRIPT:
            return !!integrationsLoad.value?.postscript?.integration;

          case ConversationPlatform.TIKTOK:
            return !!integrationsLoad.value?.tiktok?.integration;

          case ConversationPlatform.MOBILE_MESSAGE:
            return true;

          default:
            assertNever(platform);
        }
      },
    [team, emailIntegrationsLoad.value, integrationsLoad.value],
  );

  const connectedChannels = useMemo(
    () =>
      CONNECTABLE_PLATFORMS.filter((platform) =>
        isPlatformConnected(platform),
      ).flatMap((platform) => platformChannels[platform] ?? []),
    [isPlatformConnected],
  );

  return {
    isPlatformConnected,
    connectedChannels,
    pending: integrationsLoad.pending,
  };
}
